import { useState, useEffect, useRef } from 'react';

import './wallboard-dashboard.css';

import {
    Widget,
    ProgressCircle,
    LoadingIndicator,
} from '../../../components/components';

import { useInterval } from '../../../interval';
import { calcDuration, calcPercent } from '../puzzel-queue';
import { loadWallboardAgentsForQueues, loadWallboardQueues } from '../wallboards';

function transformQueueToViewData(queue) {
    const data = {
        id: queue.id,
        description: queue.description,
        waitingCalls: queue.queueSize,
        abandonedCalls: queue.callsAbandonedToday,
        answeredCalls: queue.callsAnsweredToday,
        totalCalls: queue.callsOfferedToday,
        agentsLoggedOn: queue.agentsLoggedOn,
        agentsReady: queue.agentsReady,
        agentsUnavailable: queue.agentsUnavailable,
        agentsInPause: queue.agentsInPause,
        sla: queue.callsOfferedToday === 0 ? 100 : calcPercent(queue.callsAnsweredWithinSla, queue.callsOfferedToday),
        answeredRate: queue.callsOfferedToday === 0 ? 100 : calcPercent(queue.callsAnsweredToday, queue.callsOfferedToday),
        abandonedRate: queue.callsOfferedToday === 0 ? 0 : calcPercent(queue.callsAbandonedToday, queue.callsOfferedToday),
        avgWaitingTime: queue.waitTimeAverageSeconds,
        maxWaitingTime: queue.waitTimeMaxSeconds,
        waitingCallbackCalls: queue.queueSizeCiq,
    };
    return data;
}

function summerizeQueues(queues) {
    const queue = queues.reduce((sum, queue) => {
        for (const key in queue) {
            if (!sum[key]) {
                sum[key] = queue[key]
                continue;
            }

            if (key === "waitTimeMaxSeconds") {
                if (sum[key] < queue[key]) {
                    sum[key] = queue[key];
                }
                continue;
            }

            sum[key] = sum[key] + queue[key];
        }
        return sum;
    }, {});

    queue.waitTimeAverageSeconds = calcAverageWaitTime(queue.waitTimeAverageSeconds, queues);

    return queue;
}

function calcAverageWaitTime(value, queues) {
    const numberOfQueuesWithWaitTime = queues.filter(queue => queue.waitTimeAverageSeconds > 0).length;
    if (numberOfQueuesWithWaitTime < 1) {
        return value;
    }

    return value / numberOfQueuesWithWaitTime;
}

function Progress(props) {
    const className = props.class ? props.class : "";

    return (
        <div className={`progress-container ${className}`}>
            <div>
                <p className="small-number">{`${props.value}%` || "0%"}</p>
                <p className="text">{props.text}</p>
            </div>
            <ProgressCircle value={props.value} class={className} />
        </div>
    );
}

function CurrentTime() {
    const [time, setTime] = useState("00:00");
    const [date, setDate] = useState("00-00-0000");
    const [day, setDay] = useState(" ");

    useEffect(() => {
        function updateTime() {
            var now = new Date(Date.now());
            setTime(now.toLocaleTimeString("en-gb", { hour: "2-digit", minute: "2-digit" }));
            setDate(now.toLocaleDateString());
            setDay(now.toLocaleString([], { weekday: "short" }));
        }

        const timer = setInterval(() => {
            updateTime();
        }, 1000);

        updateTime();

        return () => {
            clearInterval(timer);
        };
    }, []);

    return (
        <div className="current-time-container">
            <div>
                <p className="time">{time}</p>
                <p className="date">{date}</p>
            </div>
            <p className="day">{day}</p>
        </div>
    );
}

function useWallboardQueue(refresh, customerKeys) {
    const [queue, setQueue] = useState(null);
    const [tokens, setTokens] = useState({});

    const load = useRef(async () => {
        const queues = await loadWallboardQueues(customerKeys, tokens, setTokens);
        const summery = summerizeQueues(queues);
        const queue = transformQueueToViewData(summery);
        setQueue(queue);
    });

    useInterval(() => {
        load.current();
    }, refresh * 1000);

    useEffect(() => {
        load.current();
    }, []);

    return queue;
}

function useWallboardAgents(refresh, customerKeys) {
    const [loading, setLoading] = useState(false);
    const [hasLoadedValues, setHasLoadedValues] = useState(false);
    const [loggedOn, setLoggedOn] = useState(0);
    const [available, setAvailable] = useState(0);
    const [tokens, setTokens] = useState({});

    const load = useRef(async loading => {
        if (loading) {
            return;
        }

        setLoading(true);

        const [loggedOn, available, , , pause] = await loadWallboardAgentsForQueues(customerKeys, tokens, setTokens);

        const loggedOnIncludingPaused = new Set([...loggedOn, ...pause]);

        setLoggedOn(loggedOnIncludingPaused.size);
        setAvailable(available.size);
        setLoading(false);
        setHasLoadedValues(true);
    });

    useInterval(() => {
        load.current(loading);
    }, refresh * 1000);

    useEffect(() => {
        load.current(false);
    }, []);

    return [hasLoadedValues, loggedOn, available];
}

export function DashboardWallboard({ wallboard, refresh }) {
    const queue = useWallboardQueue(refresh, wallboard.customerKeys);
    const [hasLoadedAgents, loggedOnIncludingPaused, available] = useWallboardAgents(refresh, wallboard.customerKeys);

    const slaStatus = queue?.sla < 70 ? "bad-sla" : "good-sla";

    return (
        <div className="dashboard-container">
            <Widget class="waiting-calls" noMargins="true">
                <p className="big-number centered">{queue?.waitingCalls || 0}</p>
                <p className="text centered">Waiting calls</p>
            </Widget>
            <Widget class="average-waiting-time" noMargins="true">
                <p className="small-number">
                    {calcDuration(queue?.avgWaitingTime || 0)}
                </p>
                <p className="text centered">Average waiting time</p>
            </Widget>
            <Widget class="max-waiting-time" noMargins="true">
                <p className="small-number">
                    {calcDuration(queue?.maxWaitingTime || 0)}
                </p>
                <p className="text centered">Max waiting time</p>
            </Widget>
            <Widget class="abandoned-calls" noMargins="true" variant="warning">
                <p className="small-number centered">{queue?.abandonedCalls || 0}</p>
                <p className="text centered">Abandoned calls</p>
            </Widget>
            <Widget class="answered-calls" noMargins="true">
                <p className="small-number centered">{queue?.answeredCalls || 0}</p>
                <p className="text centered">Answered calls</p>
            </Widget>
            <Widget class="total-calls" noMargins="true" variant="notice">
                <p className="small-number centered">{queue?.totalCalls || 0}</p>
                <p className="text centered">Total calls</p>
            </Widget>
            <Widget class="agents-logged-in" noMargins="true">
                <Loader isLoading={!hasLoadedAgents}>
                    <p className="small-number centered">{loggedOnIncludingPaused}</p>
                </Loader>
                <p className="text">Agents logged on</p>
            </Widget>
            <Widget class="available-agents" noMargins="true">
                <Loader isLoading={!hasLoadedAgents}>
                    <p className="small-number centered">{available}</p>
                </Loader>
                <p className="text">Available agents</p>
            </Widget>
            <Widget class="sla" noMargins="true">
                <Progress value={queue?.sla || 0} text="SLA" class={slaStatus} />
            </Widget>
            <Widget class="answered-rate" noMargins="true">
                <Progress value={queue?.answeredRate || 0} text="Answered rate" />
            </Widget>
            <Widget class="abandoned-rate" noMargins="true">
                <Progress value={queue?.abandonedRate || 0} text="Abandoned rate" />
            </Widget>
            <Widget class="current-time" noMargins="true">
                <CurrentTime />
            </Widget>
        </div>
    );
}

function Loader(props) {
    if (props.isLoading) {
        return (
            <LoadingIndicator />
        );
    }

    return props.children;
}